import React, { useState, useEffect } from 'react';
import { fetchOrdersFromFirestore } from './services/orderService.firestore';
import userDataService from './services/userDataService';
import { OrderAdapter } from './adapters/OrderAdapter';
import { useAuth } from './context/AuthContext';
import './App.css';

const DemoPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const { userData, currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [statsSummary, setStatsSummary] = useState({totalSales: 0, orderCount: 0, averageOrderValue: 0, conversionRate: 0});
  const [dailyStats, setDailyStats] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    async function loadRealData() {
      try {
        // Obtener storeId con la estrategia robusta de fallback
        const storeId = userDataService?.getStoreId?.() || 
                     userData?.tiendaId || 
                     userData?.tiendas?.[0]?.id || 
                     currentUser?.uid;
                     
        if (!storeId) {
          console.error('No se pudo determinar el storeId');
          return;
        }
        
        // Cargar órdenes reales desde Firestore
        const result = await fetchOrdersFromFirestore({storeId});
        const realOrders = result.orders || [];
        
        // Normalizar órdenes con el adaptador
        const normalizedOrders = OrderAdapter.normalizeMany(realOrders);
        setOrders(normalizedOrders);
        
        // Calcular estadísticas a partir de datos reales
        if (normalizedOrders.length > 0) {
          // Usuario real
          setUser({
            tiendaSubdominio: userData?.tiendaSubdominio || 'tienda',
            displayName: userData?.displayName || currentUser?.displayName || 'Mi Tienda',
            plan: userData?.plan || 'Pro',
            status: 'active'
          });
          
          // Calcular estadísticas reales
          const totalSales = normalizedOrders.reduce((sum, order) => sum + (parseFloat(order.total) || 0), 0);
          const orderCount = normalizedOrders.length;
          
          setStatsSummary({
            totalSales,
            orderCount,
            averageOrderValue: orderCount ? totalSales / orderCount : 0,
            conversionRate: 2.5, // Este dato no puede calcularse solo con órdenes
            periodCompare: {
              totalSales: 0,
              orderCount: 0,
              averageOrderValue: 0,
              conversionRate: 0
            }
          });
          
          // Generar estadísticas diarias basadas en datos reales
          const dailyMap = {};
          normalizedOrders.forEach(order => {
            const date = order.date?.split('T')[0];
            if (date) {
              if (!dailyMap[date]) {
                dailyMap[date] = { date, sales: 0, orders: 0 };
              }
              dailyMap[date].sales += parseFloat(order.total) || 0;
              dailyMap[date].orders += 1;
            }
          });
          
          setDailyStats(Object.values(dailyMap).sort((a, b) => a.date.localeCompare(b.date)));
          
          // Calcular productos más vendidos
          const productMap = {};
          normalizedOrders.forEach(order => {
            (order.items || []).forEach(item => {
              const productId = item.productId || item.id;
              if (!productMap[productId]) {
                productMap[productId] = {
                  id: productId,
                  name: item.title || item.name,
                  sales: 0,
                  quantity: 0
                };
              }
              productMap[productId].sales += (parseFloat(item.price) * item.quantity) || 0;
              productMap[productId].quantity += parseInt(item.quantity) || 0;
            });
          });
          
          setTopProducts(
            Object.values(productMap)
              .sort((a, b) => b.sales - a.sales)
              .slice(0, 5)
          );
        }
      } catch (error) {
        console.error('Error cargando datos reales:', error);
      } finally {
        setLoading(false);
      }
    }
    
    loadRealData();
  }, [currentUser, userData]);

  // Helper function to format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(value);
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR');
  };
  
  // Helper function to format time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR') + ' ' + date.toLocaleTimeString('es-AR', {hour: '2-digit', minute:'2-digit'});
  };

  return (
    <div className="app">
      <header className="header">
        <div className="logo">Nubelytics</div>
        <div className="user-info">
          <span className="store-name">{user.displayName}</span>
          <span className="plan-badge">{user.plan}</span>
        </div>
      </header>
      
      <div className="sidebar">
        <div className={`sidebar-item ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>
          Dashboard
        </div>
        <div className={`sidebar-item ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => setActiveTab('orders')}>
          Órdenes
        </div>
        <div className={`sidebar-item ${activeTab === 'products' ? 'active' : ''}`} onClick={() => setActiveTab('products')}>
          Productos
        </div>
        <div className={`sidebar-item ${activeTab === 'customers' ? 'active' : ''}`} onClick={() => setActiveTab('customers')}>
          Clientes
        </div>
        <div className={`sidebar-item ${activeTab === 'alerts' ? 'active' : ''}`} onClick={() => setActiveTab('alerts')}>
          Alertas
        </div>
        <div className={`sidebar-item ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
          Configuración
        </div>
      </div>
      
      <main className="content">
        {activeTab === 'dashboard' && (
          <div className="dashboard">
            <h1>Dashboard</h1>
            <p className="period-info">Período: Últimos 7 días (25 May - 31 May 2025)</p>
            
            <div className="stats-cards">
              <div className="stat-card">
                <h3>Ventas Totales</h3>
                <div className="stat-value">{formatCurrency(statsSummary.totalSales)}</div>
                <div className={`stat-change ${statsSummary.periodCompare.totalSales >= 0 ? 'positive' : 'negative'}`}>
                  {statsSummary.periodCompare.totalSales >= 0 ? '+' : ''}{statsSummary.periodCompare.totalSales}%
                </div>
              </div>
              
              <div className="stat-card">
                <h3>Órdenes</h3>
                <div className="stat-value">{statsSummary.orderCount}</div>
                <div className={`stat-change ${statsSummary.periodCompare.orderCount >= 0 ? 'positive' : 'negative'}`}>
                  {statsSummary.periodCompare.orderCount >= 0 ? '+' : ''}{statsSummary.periodCompare.orderCount}%
                </div>
              </div>
              
              <div className="stat-card">
                <h3>Valor Promedio</h3>
                <div className="stat-value">{formatCurrency(statsSummary.averageOrderValue)}</div>
                <div className={`stat-change ${statsSummary.periodCompare.averageOrderValue >= 0 ? 'positive' : 'negative'}`}>
                  {statsSummary.periodCompare.averageOrderValue >= 0 ? '+' : ''}{statsSummary.periodCompare.averageOrderValue}%
                </div>
              </div>
              
              <div className="stat-card">
                <h3>Conversión</h3>
                <div className="stat-value">{statsSummary.conversionRate}%</div>
                <div className={`stat-change ${statsSummary.periodCompare.conversionRate >= 0 ? 'positive' : 'negative'}`}>
                  {statsSummary.periodCompare.conversionRate >= 0 ? '+' : ''}{statsSummary.periodCompare.conversionRate}%
                </div>
              </div>
            </div>
            
            <div className="charts-container">
              <div className="chart">
                <h2>Ventas Diarias</h2>
                <div className="chart-placeholder">
                  {dailyStats.map((day, index) => (
                    <div key={index} className="chart-bar" style={{height: `${(day.sales / 30080) * 200}px`}}>
                      <div className="bar-amount">{formatCurrency(day.sales)}</div>
                    </div>
                  ))}
                </div>
                <div className="chart-labels">
                  {dailyStats.map((day, index) => (
                    <span key={index} className="chart-label">{day.date.slice(-2)}</span>
                  ))}
                </div>
              </div>
              
              <div className="top-products">
                <h2>Top Productos</h2>
                <table className="products-table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Ventas</th>
                      <th>Cantidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topProducts.map(product => (
                      <tr key={product.id}>
                        <td>{product.name}</td>
                        <td>{formatCurrency(product.sales)}</td>
                        <td>{product.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'orders' && (
          <div className="orders-page">
            <h1>Órdenes</h1>
            
            <div className="filters">
              <div className="date-range">
                <label>Rango de fechas</label>
                <select>
                  <option>Últimos 7 días</option>
                  <option>Últimos 30 días</option>
                  <option>Este mes</option>
                  <option>Mes anterior</option>
                  <option>Personalizado</option>
                </select>
              </div>
              
              <div className="status-filter">
                <label>Estado</label>
                <select>
                  <option>Todos</option>
                  <option>Pagados</option>
                  <option>Pendientes</option>
                  <option>Cancelados</option>
                </select>
              </div>
              
              <div className="search-filter">
                <input type="text" placeholder="Buscar por ID o cliente..." />
                <button className="search-btn">Buscar</button>
              </div>
            </div>
            
            <table className="orders-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Fecha</th>
                  <th>Cliente</th>
                  <th>Estado</th>
                  <th>Total</th>
                  <th>Items</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order.id}>
                    <td>#{order.id}</td>
                    <td>{formatDateTime(order.date)}</td>
                    <td>{order.customer}</td>
                    <td>
                      <span className={`status-badge ${order.status}`}>
                        {order.status === 'paid' ? 'Pagado' : 
                         order.status === 'pending' ? 'Pendiente' : 'Cancelado'}
                      </span>
                    </td>
                    <td>{formatCurrency(order.total)}</td>
                    <td>{order.items}</td>
                    <td>
                      <button className="action-btn">Ver</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {activeTab === 'products' && (
          <div className="products-page">
            <h1>Productos</h1>
            <p>Análisis de rendimiento de productos</p>
            
            <div className="filters">
              <div className="date-range">
                <label>Período</label>
                <select>
                  <option>Últimos 7 días</option>
                  <option>Últimos 30 días</option>
                  <option>Este mes</option>
                  <option>Mes anterior</option>
                </select>
              </div>
              
              <div className="category-filter">
                <label>Categoría</label>
                <select>
                  <option>Todas</option>
                  <option>Calzado</option>
                  <option>Ropa</option>
                  <option>Accesorios</option>
                </select>
              </div>
            </div>
            
            <div className="product-analytics">
              <div className="analytics-card">
                <h3>Más vendidos</h3>
                <div className="chart-placeholder large"></div>
              </div>
              
              <div className="analytics-card">
                <h3>Mejores conversiones</h3>
                <div className="chart-placeholder large"></div>
              </div>
            </div>
            
            <table className="products-table full">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Categoría</th>
                  <th>Vistas</th>
                  <th>Conversión</th>
                  <th>Ventas</th>
                  <th>Cantidad</th>
                  <th>Ingresos</th>
                </tr>
              </thead>
              <tbody>
                {topProducts.map((product, index) => (
                  <tr key={product.id}>
                    <td>{product.name}</td>
                    <td>{['Calzado', 'Ropa', 'Accesorios', 'Calzado', 'Accesorios'][index]}</td>
                    <td>{Math.round(product.quantity * (5 + Math.random() * 10))}</td>
                    <td>{(Math.random() * 10 + 2).toFixed(1)}%</td>
                    <td>{formatCurrency(product.sales)}</td>
                    <td>{product.quantity}</td>
                    <td>{formatCurrency(product.sales)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {activeTab === 'alerts' && (
          <div className="alerts-page">
            <h1>Alertas y Notificaciones</h1>
            <p>Configura alertas automáticas para tu tienda</p>
            
            <div className="alerts-config">
              <div className="alert-card">
                <h3>Stock Bajo</h3>
                <p>Te notifica cuando el stock de productos está por debajo del umbral definido</p>
                <div className="alert-settings">
                  <label>Umbral mínimo:</label>
                  <input type="number" value="5" />
                </div>
                <div className="alert-toggle">
                  <label>Activar:</label>
                  <input type="checkbox" checked />
                </div>
              </div>
              
              <div className="alert-card">
                <h3>Ventas diarias</h3>
                <p>Te notifica cuando las ventas diarias superan o están por debajo de cierto valor</p>
                <div className="alert-settings">
                  <label>Mínimo diario:</label>
                  <input type="number" value="10000" />
                </div>
                <div className="alert-toggle">
                  <label>Activar:</label>
                  <input type="checkbox" checked />
                </div>
              </div>
              
              <div className="alert-card">
                <h3>Productos sin movimiento</h3>
                <p>Te notifica sobre productos sin ventas en cierto período</p>
                <div className="alert-settings">
                  <label>Días sin ventas:</label>
                  <input type="number" value="30" />
                </div>
                <div className="alert-toggle">
                  <label>Activar:</label>
                  <input type="checkbox" />
                </div>
              </div>
              
              <div className="alert-card">
                <h3>Carritos abandonados</h3>
                <p>Te notifica sobre carritos abandonados que superan cierto valor</p>
                <div className="alert-settings">
                  <label>Valor mínimo:</label>
                  <input type="number" value="5000" />
                </div>
                <div className="alert-toggle">
                  <label>Activar:</label>
                  <input type="checkbox" checked />
                </div>
              </div>
            </div>
            
            <div className="notification-preferences">
              <h2>Preferencias de notificación</h2>
              <div className="notification-option">
                <input type="checkbox" checked />
                <label>Email</label>
              </div>
              <div className="notification-option">
                <input type="checkbox" />
                <label>SMS</label>
              </div>
              <div className="notification-option">
                <input type="checkbox" checked />
                <label>Panel de administración</label>
              </div>
              <div className="notification-option">
                <input type="checkbox" />
                <label>WhatsApp</label>
                <span className="pro-badge">Pro</span>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'settings' && (
          <div className="settings-page">
            <h1>Configuración</h1>
            
            <div className="settings-section">
              <h2>Plan actual</h2>
              <div className="current-plan">
                <div className="plan-header">
                  <h3>{user.plan}</h3>
                  <span className="plan-price">{user.plan === 'Free' ? '$0' : user.plan === 'Pro' ? '$19.99' : '$49.99'}/mes</span>
                </div>
                <div className="plan-features">
                  <ul>
                    {user.plan === 'Free' && (
                      <>
                        <li>Estadísticas básicas</li>
                        <li>7 días de datos</li>
                        <li>Filtros básicos</li>
                      </>
                    )}
                    {user.plan === 'Pro' && (
                      <>
                        <li>Estadísticas avanzadas</li>
                        <li>30 días de datos</li>
                        <li>10 alertas personalizadas</li>
                        <li>Análisis completo de carritos</li>
                      </>
                    )}
                    {user.plan === 'Elite' && (
                      <>
                        <li>Estadísticas premium</li>
                        <li>Datos históricos ilimitados</li>
                        <li>20 alertas personalizadas</li>
                        <li>Reportes automáticos</li>
                      </>
                    )}
                  </ul>
                </div>
                <button className="upgrade-btn">
                  {user.plan === 'Free' ? 'Actualizar Plan' : user.plan === 'Pro' ? 'Actualizar a Elite' : 'Cambiar Plan'}
                </button>
              </div>
            </div>
            
            <div className="settings-section">
              <h2>Preferencias generales</h2>
              
              <div className="setting-item">
                <label>Zona horaria</label>
                <select>
                  <option>América/Argentina/Buenos_Aires</option>
                  <option>América/Santiago</option>
                  <option>América/Mexico_City</option>
                  <option>América/Bogota</option>
                </select>
              </div>
              
              <div className="setting-item">
                <label>Moneda</label>
                <select>
                  <option>ARS - Peso Argentino</option>
                  <option>USD - Dólar Estadounidense</option>
                  <option>CLP - Peso Chileno</option>
                  <option>MXN - Peso Mexicano</option>
                </select>
              </div>
              
              <div className="setting-item">
                <label>Formato de fecha</label>
                <select>
                  <option>DD/MM/AAAA</option>
                  <option>MM/DD/AAAA</option>
                  <option>AAAA-MM-DD</option>
                </select>
              </div>
            </div>
            
            <div className="settings-section">
              <h2>Integraciones</h2>
              
              <div className="integration-item">
                <div className="integration-info">
                  <h4>Google Analytics</h4>
                  <p>Conecta tus datos de Analytics con Nubelytics</p>
                </div>
                <button className="connect-btn">Conectar</button>
              </div>
              
              <div className="integration-item">
                <div className="integration-info">
                  <h4>Facebook Ads</h4>
                  <p>Visualiza el ROI de tus campañas</p>
                </div>
                <button className="connect-btn">Conectar</button>
              </div>
              
              <div className="integration-item">
                <div className="integration-info">
                  <h4>WhatsApp Business</h4>
                  <p>Envía notificaciones por WhatsApp</p>
                </div>
                <button className="connect-btn">Conectar</button>
                <span className="pro-badge">Elite</span>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DemoPage;
